import { SearchInput } from "@/components/ui/search-input";
import type { RateResponse } from "@efes/shared";
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function formatPrice(rubles: number): string {
	return rubles.toLocaleString("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function formatChangeDate(value: string | null): string {
	if (!value) return "—";
	// Value comes as "MM/YY" from Excel (e.g. "04/24" = April 2024)
	const match = value.match(/^(\d{1,2})\/(\d{2})$/);
	if (match) {
		const [, month, year] = match;
		return `${month.padStart(2, "0")}.20${year}`;
	}
	return value;
}

const columnHelper = createColumnHelper<RateResponse>();

interface RateBookTableProps {
	rates: RateResponse[];
	total: number;
	page: number;
	pageSize: number;
	search: string;
	onSearchChange: (value: string) => void;
	onPageChange: (page: number) => void;
	isLoading: boolean;
}

/**
 * Таблица расценок с поиском и пагинацией.
 * Колонки: номер расценки, наименование работы, единица измерения, цена, дата изменения.
 */
export function RateBookTable({
	rates,
	total,
	page,
	pageSize,
	search,
	onSearchChange,
	onPageChange,
	isLoading,
}: RateBookTableProps) {
	const [searchInput, setSearchInput] = useState(search);
	const totalPages = Math.ceil(total / pageSize);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => {
			onSearchChange(searchInput);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchInput, onSearchChange]);

	useEffect(() => {
		setSearchInput(search);
	}, [search]);

	const columns = useMemo(
		() => [
			columnHelper.accessor("rateNumber", {
				header: "Номер",
				cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
			}),
			columnHelper.accessor("workName", {
				header: "Наименование работы",
				cell: (info) => <span className="text-slate-900">{info.getValue()}</span>,
			}),
			columnHelper.accessor("unit", {
				header: "Ед. изм.",
				cell: (info) => info.getValue() ?? "—",
			}),
			columnHelper.accessor("price", {
				header: "Цена (руб.)",
				cell: (info) => (
					<span className="font-mono text-right block">{formatPrice(info.getValue())}</span>
				),
			}),
			columnHelper.accessor("changeDate", {
				header: "Дата изменения",
				cell: (info) => formatChangeDate(info.getValue()),
			}),
		],
		[],
	);

	const table = useReactTable({
		data: rates,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="space-y-3">
			<SearchInput
				value={searchInput}
				onChange={(e) => setSearchInput(e.target.value)}
				placeholder="Поиск по номеру или наименованию..."
			/>

			{/* Table */}
			<div className="overflow-x-auto rounded-lg border border-slate-200">
				<table className="w-full text-sm">
					<thead className="bg-slate-50 border-b border-slate-200">
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th key={header.id} className="px-3 py-2.5 text-left font-medium text-slate-600">
										{header.isPlaceholder
											? null
											: flexRender(header.column.columnDef.header, header.getContext())}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody className="divide-y divide-slate-100">
						{isLoading ? (
							<tr>
								<td colSpan={5} className="px-3 py-8 text-center text-slate-500">
									Загрузка...
								</td>
							</tr>
						) : rates.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-8 text-center text-slate-500">
									{search ? "Ничего не найдено" : "Нет расценок"}
								</td>
							</tr>
						) : (
							table.getRowModel().rows.map((row) => (
								<tr key={row.id} className="hover:bg-slate-50 transition-colors">
									{row.getVisibleCells().map((cell) => (
										<td key={cell.id} className="px-3 py-2 text-slate-700">
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-between text-sm text-slate-600">
					<span>Всего: {total} расценок</span>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => onPageChange(page - 1)}
							disabled={page <= 1}
							className="rounded p-1 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<ChevronLeft className="h-4 w-4" />
						</button>
						<span>
							{page} / {totalPages}
						</span>
						<button
							type="button"
							onClick={() => onPageChange(page + 1)}
							disabled={page >= totalPages}
							className="rounded p-1 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<ChevronRight className="h-4 w-4" />
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
